import { useState, useEffect, useMemo } from 'react';
import { format, startOfWeek, endOfWeek, addDays, addWeeks, subWeeks, parseISO, isSameDay, setHours, setMinutes, getDay } from 'date-fns';
import { getBookings, getClients, getServices, createBooking, updateBooking, createClient, cancelBooking, rescheduleBooking, reconcileBooking, reinstateBooking, deleteBooking, getAvailability, addBlockedTime, deleteBlockedTime } from '../lib/api';
import { Button } from '../components/ui/button';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Checkbox } from '../components/ui/checkbox';
import { Slider } from '../components/ui/slider';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '../components/ui/dropdown-menu';
import { toast } from 'sonner';
import { ChevronLeft, ChevronRight, Plus, Clock, CalendarIcon, Repeat, X, UserPlus, GripVertical, AlertCircle, AlertTriangle, MoreVertical, CheckCircle, XCircle, RefreshCw, RotateCcw, Trash2, Edit, Ban } from 'lucide-react';
import { cn } from '../lib/utils';

const HOURS = Array.from({ length: 14 }, (_, i) => i + 7);
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
const MINUTE_OPTIONS = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

const COUNTRY_CODES = [
  { code: '+1', country: 'US/CA' },
  { code: '+44', country: 'UK' },
  { code: '+61', country: 'AU' },
  { code: '+64', country: 'NZ' },
  { code: '+49', country: 'DE' },
];

const getDefaultCountryCode = () => {
  const locale = navigator.language || 'en-US';
  const countryMap = { 'en-US': '+1', 'en-CA': '+1', 'en-GB': '+44', 'en-AU': '+61' };
  return countryMap[locale] || '+1';
};

export default function CalendarPage() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('week'); // 'week' or 'day'
  const [bookings, setBookings] = useState([]);
  const [allBookings, setAllBookings] = useState([]);
  const [clients, setClients] = useState([]);
  const [services, setServices] = useState([]);
  const [availability, setAvailability] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sheetOpen, setSheetOpen] = useState(false);
  
  // Drag state
  const [draggedBooking, setDraggedBooking] = useState(null);
  const [dragOverSlot, setDragOverSlot] = useState(null);
  
  // Non-working hours warning (for click-to-book)
  const [nonWorkingWarningOpen, setNonWorkingWarningOpen] = useState(false);
  const [pendingNonWorkingAction, setPendingNonWorkingAction] = useState(null);
  
  // Drag-to-restricted-slot confirmation
  const [dragConfirmOpen, setDragConfirmOpen] = useState(false);
  const [dragConfirmData, setDragConfirmData] = useState(null); // { booking, day, hour, warningType }
  
  // Cancel/Reschedule dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [bookingToCancel, setBookingToCancel] = useState(null);
  const [cancelTab, setCancelTab] = useState('cancel');
  const [cancelType, setCancelType] = useState('cancel_no_charge');
  const [chargePercent, setChargePercent] = useState(100);
  
  // Reschedule state
  const [rescheduleDate, setRescheduleDate] = useState(new Date());
  const [rescheduleHour, setRescheduleHour] = useState('9');
  const [rescheduleMinute, setRescheduleMinute] = useState('0');
  const [rescheduleDatePickerOpen, setRescheduleDatePickerOpen] = useState(false);
  const [rescheduleWithFee, setRescheduleWithFee] = useState(false);
  const [rescheduleFeePercent, setRescheduleFeePercent] = useState(0);
  
  // Add client dialog
  const [addClientOpen, setAddClientOpen] = useState(false);
  const [newClientData, setNewClientData] = useState({
    name: '', client_type: 'individual', email: '', phone_country_code: getDefaultCountryCode(), phone_number: ''
  });
  
  // Booking form state
  const [bookingClientId, setBookingClientId] = useState('');
  const [bookingServiceId, setBookingServiceId] = useState('');
  const [bookingDate, setBookingDate] = useState(new Date());
  const [bookingHour, setBookingHour] = useState('9');
  const [bookingMinute, setBookingMinute] = useState('0');
  const [bookingNotes, setBookingNotes] = useState('');
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  
  // Edit booking state
  const [editingBooking, setEditingBooking] = useState(null);
  
  // Recurrence
  const [isRecurring, setIsRecurring] = useState(false);
  const [recurrenceType, setRecurrenceType] = useState('weekly');
  const [customDays, setCustomDays] = useState(7);
  const [recurrenceEndDate, setRecurrenceEndDate] = useState(null);
  const [recurrenceEndPickerOpen, setRecurrenceEndPickerOpen] = useState(false);
  
  // Multi-hour selection state
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null); // { day, hour }
  const [selectionEnd, setSelectionEnd] = useState(null); // { day, hour }
  const [bookingDuration, setBookingDuration] = useState(60); // minutes
  
  // Quick Block state
  const [quickBlockOpen, setQuickBlockOpen] = useState(false);
  const [quickBlockData, setQuickBlockData] = useState({ title: '', date: null, startHour: 9, duration: 60, recurring: false });

  const weekStart = useMemo(() => startOfWeek(currentDate, { weekStartsOn: 0 }), [currentDate]);
  const weekEnd = useMemo(() => endOfWeek(currentDate, { weekStartsOn: 0 }), [currentDate]);
  const weekDays = useMemo(() => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)), [weekStart]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [bookingsData, clientsData, servicesData, availabilityData] = await Promise.all([
        getBookings({ from_utc: weekStart.toISOString(), to_utc: weekEnd.toISOString() }),
        getClients(),
        getServices(),
        getAvailability(),
      ]);
      setBookings(bookingsData);
      setAllBookings(bookingsData);
      setClients(clientsData.filter(c => c.is_active !== false));
      setServices(servicesData.filter(s => s.is_active));
      setAvailability(availabilityData);
    } catch (err) {
      toast.error('Failed to load calendar data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [weekStart.toISOString()]);

  const handlePrevWeek = () => setCurrentDate(subWeeks(currentDate, 1));
  const handleNextWeek = () => setCurrentDate(addWeeks(currentDate, 1));
  const handlePrevDay = () => setCurrentDate(addDays(currentDate, -1));
  const handleNextDay = () => setCurrentDate(addDays(currentDate, 1));
  const handleToday = () => setCurrentDate(new Date());

  const resetBookingForm = () => {
    setBookingClientId(''); setBookingServiceId(''); setBookingDate(new Date());
    setBookingHour('9'); setBookingMinute('0'); setBookingNotes('');
    setIsRecurring(false); setRecurrenceType('weekly'); setCustomDays(7); setRecurrenceEndDate(null);
    setBookingDuration(60);
    setEditingBooking(null);
  };

  // Open edit booking sheet
  const openEditBooking = (booking) => {
    const startDate = parseISO(booking.start_at_utc);
    setEditingBooking(booking);
    setBookingClientId(booking.client_id);
    setBookingServiceId(booking.service_id);
    setBookingDate(startDate);
    setBookingHour(startDate.getHours().toString());
    setBookingMinute(startDate.getMinutes().toString());
    setBookingDuration(booking.duration_min || 60);
    setBookingNotes(booking.notes || '');
    setIsRecurring(false);
    setSheetOpen(true);
  };

  // Multi-hour selection handlers
  const handleSelectionStart = (day, hour) => {
    if (draggedBooking) return; // Don't start selection if dragging a booking
    setIsSelecting(true);
    setSelectionStart({ day, hour });
    setSelectionEnd({ day, hour });
  };

  const handleSelectionMove = (day, hour) => {
    if (!isSelecting || !selectionStart) return;
    // Only allow selection within the same day
    if (selectionStart.day.toISOString() === day.toISOString()) {
      setSelectionEnd({ day, hour });
    }
  };

  const handleSelectionEnd = () => {
    if (!isSelecting || !selectionStart || !selectionEnd) {
      setIsSelecting(false);
      setSelectionStart(null);
      setSelectionEnd(null);
      return;
    }
    
    const startHour = Math.min(selectionStart.hour, selectionEnd.hour);
    const endHour = Math.max(selectionStart.hour, selectionEnd.hour) + 1;
    const duration = (endHour - startHour) * 60;
    
    // Open booking sheet with the selected duration
    resetBookingForm();
    setBookingDate(selectionStart.day);
    setBookingHour(startHour.toString());
    setBookingMinute('0');
    setBookingDuration(duration);
    setSheetOpen(true);
    
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  };

  const isSlotInSelection = (day, hour) => {
    if (!isSelecting || !selectionStart || !selectionEnd) return false;
    if (selectionStart.day.toISOString() !== day.toISOString()) return false;
    const minHour = Math.min(selectionStart.hour, selectionEnd.hour);
    const maxHour = Math.max(selectionStart.hour, selectionEnd.hour);
    return hour >= minHour && hour <= maxHour;
  };

  // Quick Block functions
  const openQuickBlock = (day, hour, duration = 60) => {
    setQuickBlockData({
      title: '',
      date: day,
      startHour: hour,
      duration: duration,
      recurring: false,
    });
    setQuickBlockOpen(true);
  };

  const handleQuickBlock = async () => {
    if (!quickBlockData.title.trim()) {
      toast.error('Please enter a title for the block');
      return;
    }
    
    const startH = quickBlockData.startHour;
    const endH = startH + Math.ceil(quickBlockData.duration / 60);
    const dateStr = format(quickBlockData.date, 'yyyy-MM-dd');
    
    const blockData = {
      name: quickBlockData.title,
      block_type: quickBlockData.recurring ? 'recurring' : 'one_time',
      start_time: `${startH.toString().padStart(2, '0')}:00`,
      end_time: `${endH.toString().padStart(2, '0')}:00`,
    };
    
    if (quickBlockData.recurring) {
      blockData.days_of_week = [getDay(quickBlockData.date)];
    } else {
      blockData.start_date = dateStr;
      blockData.end_date = dateStr;
    }
    
    try {
      await addBlockedTime(blockData);
      toast.success('Time blocked successfully');
      setQuickBlockOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to block time');
    }
  };

  const handleRemoveBlock = async (blockId) => {
    try {
      await deleteBlockedTime(blockId);
      toast.success('Block removed');
      fetchData();
    } catch (err) {
      toast.error('Failed to remove block');
    }
  };

  // Get block info for a specific slot
  const getBlockInfo = (day, hour) => {
    if (!availability?.blocked_times) return null;
    const dayIndex = getDay(day);
    const dateStr = format(day, 'yyyy-MM-dd');
    
    return availability.blocked_times.find(block => {
      const blockStartH = parseInt(block.start_time?.split(':')[0] || '0');
      const blockEndH = parseInt(block.end_time?.split(':')[0] || '24');
      if (hour < blockStartH || hour >= blockEndH) return false;
      
      if (block.block_type === 'recurring') {
        return block.days_of_week?.includes(dayIndex);
      } else {
        // One-time block - check if date is within range
        return block.start_date === dateStr || block.end_date === dateStr || 
               (block.start_date <= dateStr && block.end_date >= dateStr);
      }
    });
  };

  // Check if time is outside working hours (grey) - weekends, before/after work
  const isOutsideWorkingHours = (day, hour) => {
    if (!availability) return false;
    const dayName = DAY_NAMES[getDay(day)];
    const wh = availability.working_hours?.[dayName];
    if (!wh?.enabled) return true; // Day is not a working day
    const startH = parseInt(wh.start?.split(':')[0] || '9');
    const endH = parseInt(wh.end?.split(':')[0] || '17');
    return hour < startH || hour >= endH;
  };

  // Check if time is blocked (red) - lunch, holidays, manually blocked times
  const isBlockedTime = (day, hour) => {
    if (!availability?.blocked_times) return false;
    const slotStart = setMinutes(setHours(day, hour), 0);
    const slotEnd = setMinutes(setHours(day, hour + 1), 0);
    const dayIndex = getDay(day); // 0 = Sunday, 1 = Monday, etc.
    
    return availability.blocked_times.some(block => {
      if (block.block_type === 'recurring') {
        // Recurring blocks (e.g., lunch every day)
        // Backend stores days as integers: 0=Sunday, 1=Monday, etc.
        if (!block.days_of_week?.includes(dayIndex)) return false;
        const blockStartH = parseInt(block.start_time?.split(':')[0] || '12');
        const blockEndH = parseInt(block.end_time?.split(':')[0] || '13');
        return hour >= blockStartH && hour < blockEndH;
      } else {
        // One-time blocks (e.g., holidays)
        if (!block.start_date || !block.end_date) return false;
        const dateStr = format(day, 'yyyy-MM-dd');
        if (dateStr < block.start_date || dateStr > block.end_date) return false;
        const blockStartH = parseInt(block.start_time?.split(':')[0] || '0');
        const blockEndH = parseInt(block.end_time?.split(':')[0] || '24');
        return hour >= blockStartH && hour < blockEndH;
      }
    });
  };

  // Combined check for any unavailable time (for warnings)
  const isTimeBlocked = (day, hour) => {
    return isOutsideWorkingHours(day, hour) || isBlockedTime(day, hour);
  };

  // State for blocked time warning
  const [blockedTimeWarningOpen, setBlockedTimeWarningOpen] = useState(false);

  const openBookingSheet = (date = null, hour = null, skipWarning = false) => {
    // Check if it's outside working hours (grey) - existing warning
    if (date && hour !== null && !skipWarning && isOutsideWorkingHours(date, hour)) {
      setPendingNonWorkingAction({ type: 'book', date, hour });
      setNonWorkingWarningOpen(true);
      return;
    }
    // Check if it's blocked time (red) - new warning
    if (date && hour !== null && !skipWarning && isBlockedTime(date, hour)) {
      setPendingNonWorkingAction({ type: 'book-blocked', date, hour });
      setBlockedTimeWarningOpen(true);
      return;
    }
    resetBookingForm();
    if (date) {
      setBookingDate(date);
      if (hour !== null) {
        setBookingHour(hour.toString());
        setBookingMinute('0');
      }
    }
    setSheetOpen(true);
  };

  const confirmNonWorkingAction = () => {
    setNonWorkingWarningOpen(false);
    const action = pendingNonWorkingAction;
    setPendingNonWorkingAction(null);
    if (action?.type === 'book') {
      openBookingSheet(action.date, action.hour, true);
    }
  };

  const confirmBlockedTimeAction = () => {
    setBlockedTimeWarningOpen(false);
    const action = pendingNonWorkingAction;
    setPendingNonWorkingAction(null);
    if (action?.type === 'book-blocked') {
      openBookingSheet(action.date, action.hour, true);
    }
  };

  const handleCreateBooking = async (e) => {
    e.preventDefault();
    
    // Check if "Other" service selected
    if (bookingServiceId === 'other') {
      toast.error('Please select a service or create one in Services settings');
      return;
    }
    
    let bookingDateTime = setHours(bookingDate, parseInt(bookingHour));
    bookingDateTime = setMinutes(bookingDateTime, parseInt(bookingMinute));
    
    const payload = {
      client_id: bookingClientId, service_id: bookingServiceId,
      start_at_utc: bookingDateTime.toISOString(), notes: bookingNotes || null,
      duration_min: bookingDuration,
    };
    
    try {
      if (editingBooking) {
        // Update existing booking
        await updateBooking(editingBooking.id, payload);
        toast.success('Booking updated');
      } else {
        // Create new booking(s)
        if (isRecurring && recurrenceEndDate) {
          payload.recurrence_type = recurrenceType;
          if (recurrenceType === 'custom') payload.recurrence_interval_days = customDays;
          payload.recurrence_end_date = recurrenceEndDate.toISOString();
        }
        const result = await createBooking(payload);
        const count = Array.isArray(result) ? result.length : 1;
        toast.success(`${count} booking${count > 1 ? 's' : ''} created`);
      }
      setSheetOpen(false); resetBookingForm(); fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save booking');
    }
  };

  const handleAddClient = async () => {
    if (!newClientData.name) { toast.error('Name is required'); return; }
    try {
      const created = await createClient(newClientData);
      setClients(prev => [...prev, created]);
      setBookingClientId(created.id);
      setAddClientOpen(false);
      setNewClientData({ name: '', client_type: 'individual', email: '', phone_country_code: getDefaultCountryCode(), phone_number: '' });
      toast.success('Client added');
    } catch (err) { toast.error('Failed to add client'); }
  };

  const openCancelDialog = (booking) => {
    setBookingToCancel(booking); setCancelTab('cancel'); setCancelType('cancel_no_charge');
    setChargePercent(100); setRescheduleDate(new Date()); setRescheduleHour('9');
    setRescheduleMinute('0'); setRescheduleWithFee(false); setRescheduleFeePercent(0);
    setCancelDialogOpen(true);
  };

  const handleCancelBooking = async () => {
    if (!bookingToCancel) return;
    try {
      await cancelBooking(bookingToCancel.id, { cancel_type: cancelType, charge_percent: chargePercent });
      toast.success('Booking cancelled');
      setCancelDialogOpen(false); setBookingToCancel(null); fetchData();
    } catch (err) { toast.error('Failed to cancel booking'); }
  };

  const handleRescheduleBooking = async () => {
    if (!bookingToCancel) return;
    let newDateTime = setHours(rescheduleDate, parseInt(rescheduleHour));
    newDateTime = setMinutes(newDateTime, parseInt(rescheduleMinute));
    
    if (isSlotConflict(rescheduleDate, parseInt(rescheduleHour), bookingToCancel.id)) {
      toast.error('Selected time conflicts with another booking'); return;
    }
    
    try {
      await rescheduleBooking(bookingToCancel.id, { new_start_at_utc: newDateTime.toISOString() });
      if (rescheduleWithFee && rescheduleFeePercent > 0) {
        await cancelBooking(bookingToCancel.id, { cancel_type: 'cancel_charge', charge_percent: rescheduleFeePercent });
      }
      toast.success('Booking rescheduled');
      setCancelDialogOpen(false); setBookingToCancel(null); fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reschedule'); }
  };

  const handleReconcileBooking = async (bookingId) => {
    try {
      await reconcileBooking(bookingId);
      toast.success('Booking marked as completed');
      fetchData();
    } catch (err) {
      const detail = err.response?.data?.detail;
      if (detail && detail.includes('future session')) {
        toast.error(detail, { duration: 5000 });
      } else {
        toast.error('Failed to reconcile booking');
      }
    }
  };

  // Reinstate warning dialog state
  const [reinstateWarningOpen, setReinstateWarningOpen] = useState(false);
  const [bookingToReinstate, setBookingToReinstate] = useState(null);

  const handleReinstateBooking = async (booking) => {
    setBookingToReinstate(booking);
    setReinstateWarningOpen(true);
  };

  const confirmReinstateBooking = async () => {
    if (!bookingToReinstate) return;
    try {
      await reinstateBooking(bookingToReinstate.id);
      toast.success('Booking reinstated');
      setReinstateWarningOpen(false);
      setBookingToReinstate(null);
      fetchData();
    } catch (err) { 
      toast.error(err.response?.data?.detail || 'Failed to reinstate booking'); 
    }
  };

  const handleDeleteBooking = async (bookingId) => {
    if (!confirm('Are you sure you want to permanently remove this booking?')) return;
    try {
      await deleteBooking(bookingId);
      toast.success('Booking removed');
      fetchData();
    } catch (err) { toast.error('Failed to remove booking'); }
  };

  const isSlotConflict = (date, hour, excludeBookingId = null) => {
    const slotStart = setMinutes(setHours(date, hour), 0);
    const slotEnd = setMinutes(setHours(date, hour + 1), 0);
    return allBookings.some(b => {
      if (excludeBookingId && b.id === excludeBookingId) return false;
      if (b.status === 'cancelled' || b.status === 'no_show') return false;
      const bStart = parseISO(b.start_at_utc);
      const bEnd = parseISO(b.end_at_utc);
      return bStart < slotEnd && bEnd > slotStart;
    });
  };

  const handleDragStart = (e, booking) => {
    setDraggedBooking(booking);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, day, hour) => {
    e.preventDefault();
    setDragOverSlot({ day, hour });
  };

  const executeDrop = async (day, hour, skipWarning = false) => {
    if (!draggedBooking) return;
    
    const isNonWorking = isOutsideWorkingHours(day, hour);
    const isBlocked = isBlockedTime(day, hour);
    
    // If restricted slot, capture booking reference and open confirmation dialog
    if (!skipWarning && (isNonWorking || isBlocked)) {
      setDragConfirmData({
        booking: draggedBooking,
        day,
        hour,
        warningType: isBlocked ? 'blocked' : 'non-working',
      });
      setDragConfirmOpen(true);
      setDraggedBooking(null);
      setDragOverSlot(null);
      return;
    }
    
    const newStart = setMinutes(setHours(day, hour), 0);
    try {
      await rescheduleBooking(draggedBooking.id, { new_start_at_utc: newStart.toISOString() });
      toast.success('Booking rescheduled');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reschedule'); }
    setDraggedBooking(null); setDragOverSlot(null);
  };

  const confirmDragToRestricted = async () => {
    if (!dragConfirmData) return;
    const { booking, day, hour } = dragConfirmData;
    const newStart = setMinutes(setHours(day, hour), 0);
    try {
      await rescheduleBooking(booking.id, { new_start_at_utc: newStart.toISOString() });
      toast.success('Booking rescheduled');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to reschedule'); }
    setDragConfirmOpen(false);
    setDragConfirmData(null);
  };

  const handleDrop = (e, day, hour) => {
    e.preventDefault();
    executeDrop(day, hour);
  };

  const handleDragEnd = () => { setDraggedBooking(null); setDragOverSlot(null); };

  const getBookingsForDay = (day) => bookings.filter(b => isSameDay(parseISO(b.start_at_utc), day));

  const getBookingPosition = (booking) => {
    const start = parseISO(booking.start_at_utc);
    const startOffset = (start.getHours() - 7) * 60 + start.getMinutes();
    return { top: (startOffset / 60) * 60, height: Math.max((booking.duration_min / 60) * 60, 30) };
  };

  const formatHour = (h) => {
    h = parseInt(h);
    if (h === 0) return '12 AM';
    if (h === 12) return '12 PM';
    return h < 12 ? `${h} AM` : `${h - 12} PM`;
  };

  const isRescheduleSlotConflict = useMemo(() => {
    if (!bookingToCancel) return false;
    return isSlotConflict(rescheduleDate, parseInt(rescheduleHour), bookingToCancel.id);
  }, [rescheduleDate, rescheduleHour, bookingToCancel, allBookings]);

  return (
    <div className="flex flex-col h-full" data-testid="calendar-page">
      {/* Header */}
      <div className="px-8 py-4 border-b border-border bg-background/95 backdrop-blur-sm flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-heading font-bold">Calendar</h1>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="icon" onClick={viewMode === 'week' ? handlePrevWeek : handlePrevDay}>
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button variant="outline" onClick={handleToday}>Today</Button>
            <Button variant="outline" size="icon" onClick={viewMode === 'week' ? handleNextWeek : handleNextDay}>
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
          <span className="text-lg font-medium">
            {viewMode === 'week' 
              ? `${format(weekStart, 'MMM d')} - ${format(weekEnd, 'MMM d, yyyy')}`
              : format(currentDate, 'EEEE, MMMM d, yyyy')
            }
          </span>
        </div>
        <div className="flex items-center gap-3">
          {/* View Toggle */}
          <div className="flex items-center border rounded-lg p-1 bg-muted/30">
            <Button 
              variant={viewMode === 'week' ? 'default' : 'ghost'} 
              size="sm" 
              className="h-7 px-3 text-xs"
              onClick={() => setViewMode('week')}
            >
              Week
            </Button>
            <Button 
              variant={viewMode === 'day' ? 'default' : 'ghost'} 
              size="sm" 
              className="h-7 px-3 text-xs"
              onClick={() => setViewMode('day')}
            >
              Day
            </Button>
          </div>
          <Button onClick={() => openBookingSheet()}><Plus className="w-4 h-4 mr-2" /> New Booking</Button>
        </div>
      </div>

      {/* Non-Working Hours Warning */}
      <Dialog open={nonWorkingWarningOpen} onOpenChange={setNonWorkingWarningOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-amber-500" />Outside Working Hours</DialogTitle>
            <DialogDescription>This time slot is outside your configured working hours. Are you sure you want to proceed?</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setNonWorkingWarningOpen(false); setPendingNonWorkingAction(null); setDraggedBooking(null); }}>Cancel</Button>
            <Button onClick={confirmNonWorkingAction}>Proceed Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Booking Sheet */}
      <Sheet open={sheetOpen} onOpenChange={(open) => { setSheetOpen(open); if (!open) resetBookingForm(); }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          <SheetHeader><SheetTitle>{editingBooking ? 'Edit Booking' : 'New Booking'}</SheetTitle></SheetHeader>
          <form onSubmit={handleCreateBooking} className="space-y-5 mt-6">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label>Client</Label>
                <Button type="button" variant="ghost" size="sm" onClick={() => setAddClientOpen(true)}><UserPlus className="w-4 h-4 mr-1" /> Add New</Button>
              </div>
              <Select value={bookingClientId} onValueChange={setBookingClientId} required>
                <SelectTrigger><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>{clients.map(c => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.client_type === 'business' && c.business_name ? c.business_name : c.name}
                  </SelectItem>
                ))}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Service</Label>
              <Select value={bookingServiceId} onValueChange={(serviceId) => {
                setBookingServiceId(serviceId);
                // Auto-set duration based on service's default duration
                if (serviceId && serviceId !== 'other') {
                  const service = services.find(s => s.id === serviceId);
                  if (service && service.default_duration_min) {
                    setBookingDuration(service.default_duration_min);
                  }
                }
              }} required>
                <SelectTrigger><SelectValue placeholder="Select service" /></SelectTrigger>
                <SelectContent>
                  {services.map(s => (
                    <SelectItem key={s.id} value={s.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: s.color }} />
                        {s.name}
                      </div>
                    </SelectItem>
                  ))}
                  <SelectItem value="other">
                    <div className="flex items-center gap-2 text-muted-foreground">
                      <div className="w-3 h-3 rounded-full bg-gray-400" />
                      Other (Custom)
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
              {bookingServiceId === 'other' && (
                <div className="flex items-start gap-2 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-sm">
                  <AlertTriangle className="w-4 h-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="text-amber-800">
                    <span className="font-medium">Manual invoice required.</span> Since "Other" is not a configured service, you'll need to create a manual invoice for this booking. Consider adding this service in Settings → Services.
                  </div>
                </div>
              )}
            </div>
            <div className="space-y-2">
              <Label>Date</Label>
              <Popover open={datePickerOpen} onOpenChange={setDatePickerOpen}>
                <PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" />{format(bookingDate, "PPP")}</Button></PopoverTrigger>
                <PopoverContent className="w-auto p-0"><Calendar mode="single" selected={bookingDate} onSelect={d => { if(d) { setBookingDate(d); setDatePickerOpen(false); }}} /></PopoverContent>
              </Popover>
            </div>
            <div className="space-y-2">
              <Label>Time</Label>
              <div className="flex gap-3">
                <Select value={bookingHour} onValueChange={setBookingHour}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{HOUR_OPTIONS.map(h => <SelectItem key={h} value={h.toString()}>{formatHour(h)}</SelectItem>)}</SelectContent></Select>
                <Select value={bookingMinute} onValueChange={setBookingMinute}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MINUTE_OPTIONS.map(m => <SelectItem key={m} value={m.toString()}>:{m.toString().padStart(2,'0')}</SelectItem>)}</SelectContent></Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Duration</Label>
              <Select value={bookingDuration.toString()} onValueChange={v => setBookingDuration(parseInt(v))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="15">15 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="45">45 minutes</SelectItem>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="90">1.5 hours</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                  <SelectItem value="180">3 hours</SelectItem>
                  <SelectItem value="240">4 hours</SelectItem>
                  <SelectItem value="300">5 hours</SelectItem>
                  <SelectItem value="360">6 hours</SelectItem>
                  <SelectItem value="480">8 hours</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">Tip: Drag across multiple hours on the calendar to quickly select duration</p>
            </div>
            <div className="space-y-3 p-4 border rounded-lg">
              <div className="flex items-center space-x-2"><Checkbox id="recurring" checked={isRecurring} onCheckedChange={setIsRecurring} /><Label htmlFor="recurring"><Repeat className="w-4 h-4 inline mr-1" />Recurring</Label></div>
              {isRecurring && (
                <div className="space-y-3 pt-2">
                  <Select value={recurrenceType} onValueChange={setRecurrenceType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="fortnightly">Fortnightly</SelectItem><SelectItem value="monthly">Monthly</SelectItem><SelectItem value="custom">Custom</SelectItem></SelectContent></Select>
                  {recurrenceType === 'custom' && <Input type="number" min={1} max={365} value={customDays} onChange={e => setCustomDays(parseInt(e.target.value) || 7)} />}
                  <Popover open={recurrenceEndPickerOpen} onOpenChange={setRecurrenceEndPickerOpen}><PopoverTrigger asChild><Button variant="outline" className={cn("w-full justify-start", !recurrenceEndDate && "text-muted-foreground")}><CalendarIcon className="mr-2 h-4 w-4" />{recurrenceEndDate ? format(recurrenceEndDate, "PPP") : "End date"}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={recurrenceEndDate} onSelect={d => { setRecurrenceEndDate(d); setRecurrenceEndPickerOpen(false); }} /></PopoverContent></Popover>
                </div>
              )}
            </div>
            <div className="space-y-2"><Label>Notes</Label><Textarea value={bookingNotes} onChange={e => setBookingNotes(e.target.value)} /></div>
            <Button type="submit" className="w-full">{editingBooking ? 'Save Changes' : 'Create Booking'}</Button>
          </form>
        </SheetContent>
      </Sheet>

      {/* Add Client Dialog */}
      <Dialog open={addClientOpen} onOpenChange={setAddClientOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Add New Client</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2"><Label>Name *</Label><Input value={newClientData.name} onChange={e => setNewClientData(p => ({ ...p, name: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Email</Label><Input type="email" value={newClientData.email} onChange={e => setNewClientData(p => ({ ...p, email: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Phone</Label><div className="flex gap-2"><Select value={newClientData.phone_country_code} onValueChange={v => setNewClientData(p => ({ ...p, phone_country_code: v }))}><SelectTrigger className="w-24"><SelectValue /></SelectTrigger><SelectContent>{COUNTRY_CODES.map(c => <SelectItem key={c.code} value={c.code}>{c.code}</SelectItem>)}</SelectContent></Select><Input value={newClientData.phone_number} onChange={e => setNewClientData(p => ({ ...p, phone_number: e.target.value }))} /></div></div>
          </div>
          <DialogFooter><Button variant="outline" onClick={() => setAddClientOpen(false)}>Cancel</Button><Button onClick={handleAddClient}>Add</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel/Reschedule Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Manage Booking</DialogTitle></DialogHeader>
          <div className="p-3 bg-muted rounded-lg mb-4">
            <p className="font-medium">{bookingToCancel?.client_name}</p>
            <p className="text-sm text-muted-foreground">{bookingToCancel?.service_name} • {bookingToCancel && format(parseISO(bookingToCancel.start_at_utc), 'PPP p')}</p>
          </div>
          <Tabs value={cancelTab} onValueChange={setCancelTab}>
            <TabsList className="w-full"><TabsTrigger value="reschedule" className="flex-1">Reschedule</TabsTrigger><TabsTrigger value="cancel" className="flex-1">Cancel</TabsTrigger></TabsList>
            <TabsContent value="reschedule" className="space-y-4 mt-4">
              <div className="space-y-2"><Label>New Date</Label><Popover open={rescheduleDatePickerOpen} onOpenChange={setRescheduleDatePickerOpen}><PopoverTrigger asChild><Button variant="outline" className="w-full justify-start"><CalendarIcon className="mr-2 h-4 w-4" />{format(rescheduleDate, "PPP")}</Button></PopoverTrigger><PopoverContent className="w-auto p-0"><Calendar mode="single" selected={rescheduleDate} onSelect={d => { if(d) { setRescheduleDate(d); setRescheduleDatePickerOpen(false); }}} /></PopoverContent></Popover></div>
              <div className="space-y-2"><Label>New Time</Label><div className="flex gap-3"><Select value={rescheduleHour} onValueChange={setRescheduleHour}><SelectTrigger className={cn(isRescheduleSlotConflict && "border-destructive")}><SelectValue /></SelectTrigger><SelectContent>{HOUR_OPTIONS.map(h => { const conflict = bookingToCancel && isSlotConflict(rescheduleDate, h, bookingToCancel.id); return <SelectItem key={h} value={h.toString()} className={cn(conflict && "text-destructive")}>{formatHour(h)} {conflict && '(Busy)'}</SelectItem>; })}</SelectContent></Select><Select value={rescheduleMinute} onValueChange={setRescheduleMinute}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent>{MINUTE_OPTIONS.map(m => <SelectItem key={m} value={m.toString()}>:{m.toString().padStart(2,'0')}</SelectItem>)}</SelectContent></Select></div>{isRescheduleSlotConflict && <p className="text-sm text-destructive flex items-center gap-1"><AlertCircle className="w-4 h-4" /> Conflicts with another booking</p>}</div>
              <div className="space-y-3 p-4 border rounded-lg"><div className="flex items-center space-x-2"><Checkbox id="reschedule-fee" checked={rescheduleWithFee} onCheckedChange={setRescheduleWithFee} /><Label htmlFor="reschedule-fee">Charge fee</Label></div>{rescheduleWithFee && <><Label className="text-sm">Fee: {rescheduleFeePercent}%</Label><Slider value={[rescheduleFeePercent]} onValueChange={v => setRescheduleFeePercent(v[0])} min={0} max={100} step={5} /></>}</div>
              <Button onClick={handleRescheduleBooking} className="w-full" disabled={isRescheduleSlotConflict}>Reschedule</Button>
            </TabsContent>
            <TabsContent value="cancel" className="space-y-4 mt-4">
              <Select value={cancelType} onValueChange={setCancelType}><SelectTrigger><SelectValue /></SelectTrigger><SelectContent><SelectItem value="cancel_no_charge">Cancel - No Charge</SelectItem><SelectItem value="cancel_charge">Cancel - Charge Fee</SelectItem><SelectItem value="no_show_charge">No Show - Charge</SelectItem><SelectItem value="no_show_no_charge">No Show - No Charge</SelectItem></SelectContent></Select>
              {(cancelType === 'cancel_charge' || cancelType === 'no_show_charge') && <><Label>Charge: {chargePercent}%</Label><Slider value={[chargePercent]} onValueChange={v => setChargePercent(v[0])} min={0} max={100} step={5} /><p className="text-sm text-muted-foreground">Fee: ${((bookingToCancel?.rate_cents || 0) * chargePercent / 10000).toFixed(2)}</p></>}
              <Button variant="destructive" onClick={handleCancelBooking} className="w-full">Confirm Cancellation</Button>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>

      {/* Blocked Time Warning Dialog */}
      <Dialog open={blockedTimeWarningOpen} onOpenChange={setBlockedTimeWarningOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-rose-500" />
              Blocked Time
            </DialogTitle>
            <DialogDescription>
              This time slot is blocked (e.g., lunch, holiday). Are you sure you want to book here?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setBlockedTimeWarningOpen(false); setPendingNonWorkingAction(null); }}>Cancel</Button>
            <Button variant="destructive" onClick={confirmBlockedTimeAction}>Book Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Drag to Restricted Slot Confirmation Dialog */}
      <Dialog open={dragConfirmOpen} onOpenChange={(open) => { if (!open) { setDragConfirmOpen(false); setDragConfirmData(null); } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className={cn("w-5 h-5", dragConfirmData?.warningType === 'blocked' ? "text-rose-500" : "text-amber-500")} />
              {dragConfirmData?.warningType === 'blocked' ? 'Move to Blocked Time' : 'Move Outside Working Hours'}
            </DialogTitle>
            <DialogDescription>
              {dragConfirmData?.warningType === 'blocked' 
                ? 'This time slot is blocked (e.g., lunch, holiday). Are you sure you want to move this booking here?'
                : 'This time slot is outside your configured working hours. Are you sure you want to move this booking here?'
              }
            </DialogDescription>
          </DialogHeader>
          {dragConfirmData?.booking && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{dragConfirmData.booking.client_name}</p>
              <p className="text-sm text-muted-foreground">
                {dragConfirmData.booking.service_name} • Moving to {dragConfirmData.day && format(dragConfirmData.day, 'PPP')} at {dragConfirmData.hour !== undefined && formatHour(dragConfirmData.hour)}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setDragConfirmOpen(false); setDragConfirmData(null); }}>Cancel</Button>
            <Button variant={dragConfirmData?.warningType === 'blocked' ? 'destructive' : 'default'} onClick={confirmDragToRestricted}>Move Anyway</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Quick Block Dialog */}
      <Dialog open={quickBlockOpen} onOpenChange={setQuickBlockOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Ban className="w-5 h-5 text-rose-500" />
              Block Time
            </DialogTitle>
            <DialogDescription>
              Block this time slot to prevent bookings.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input 
                placeholder="e.g., Lunch, Meeting, Personal time" 
                value={quickBlockData.title} 
                onChange={e => setQuickBlockData(p => ({ ...p, title: e.target.value }))}
              />
            </div>
            <div className="flex gap-4">
              <div className="space-y-2 flex-1">
                <Label>Date</Label>
                <Input value={quickBlockData.date ? format(quickBlockData.date, 'PPP') : ''} disabled className="bg-muted" />
              </div>
              <div className="space-y-2">
                <Label>Time</Label>
                <Input value={formatHour(quickBlockData.startHour)} disabled className="bg-muted w-24" />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Duration</Label>
              <Select value={quickBlockData.duration.toString()} onValueChange={v => setQuickBlockData(p => ({ ...p, duration: parseInt(v) }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="60">1 hour</SelectItem>
                  <SelectItem value="120">2 hours</SelectItem>
                  <SelectItem value="180">3 hours</SelectItem>
                  <SelectItem value="240">4 hours</SelectItem>
                  <SelectItem value="480">8 hours (full day)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="recurring-block" 
                checked={quickBlockData.recurring} 
                onCheckedChange={v => setQuickBlockData(p => ({ ...p, recurring: v }))} 
              />
              <Label htmlFor="recurring-block" className="text-sm">
                Repeat every {quickBlockData.date ? format(quickBlockData.date, 'EEEE') : 'week'}
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuickBlockOpen(false)}>Cancel</Button>
            <Button variant="destructive" onClick={handleQuickBlock}>Block Time</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Calendar Grid */}
      <div className="flex-1 overflow-auto">
        {viewMode === 'week' ? (
          /* Week View */
          <div className="min-w-[800px]">
            <div className="grid grid-cols-8 border-b sticky top-0 bg-background z-10">
              <div className="p-3 text-xs font-medium text-muted-foreground border-r">Time</div>
              {weekDays.map(day => (
                <div key={day.toISOString()} className={`p-3 text-center border-r last:border-r-0 ${isSameDay(day, new Date()) ? 'bg-primary/5' : ''}`}>
                  <div className="text-xs font-medium text-muted-foreground uppercase">{format(day, 'EEE')}</div>
                  <div className={`text-lg font-semibold ${isSameDay(day, new Date()) ? 'text-primary' : ''}`}>{format(day, 'd')}</div>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-8" onMouseUp={handleSelectionEnd} onMouseLeave={handleSelectionEnd}>
              <div className="border-r">{HOURS.map(hour => <div key={hour} className="h-[60px] border-b px-2 py-1"><span className="text-sm font-mono text-muted-foreground">{formatHour(hour)}</span></div>)}</div>

              {weekDays.map(day => (
                <div key={day.toISOString()} className="relative border-r last:border-r-0">
                  {HOURS.map(hour => {
                    const outsideHours = isOutsideWorkingHours(day, hour);
                    const blocked = isBlockedTime(day, hour);
                    const blockInfo = blocked ? getBlockInfo(day, hour) : null;
                    const isOver = dragOverSlot?.day?.toISOString() === day.toISOString() && dragOverSlot?.hour === hour;
                    const inSelection = isSlotInSelection(day, hour);
                    return (
                      <DropdownMenu key={hour}>
                        <DropdownMenuTrigger asChild>
                          <div 
                            className={cn(
                              "h-[60px] border-b transition-colors cursor-pointer select-none relative group",
                              outsideHours && !blocked && "bg-muted/50 hover:bg-muted/70",
                              blocked && "bg-rose-500/15 hover:bg-rose-500/25",
                              !outsideHours && !blocked && !inSelection && "hover:bg-muted/30",
                              isOver && "bg-primary/20",
                              inSelection && "bg-primary/30"
                            )} 
                            onClick={(e) => { if (!isSelecting && !e.defaultPrevented) openBookingSheet(day, hour); }}
                            onMouseDown={() => handleSelectionStart(day, hour)}
                            onMouseEnter={() => handleSelectionMove(day, hour)}
                            onDragOver={e => handleDragOver(e, day, hour)} 
                            onDrop={e => handleDrop(e, day, hour)}
                            onContextMenu={(e) => e.preventDefault()}
                          >
                            {blocked && blockInfo && (
                              <div className="absolute inset-1 flex items-center justify-center">
                                <span className="text-[10px] font-medium text-rose-600 truncate px-1">{blockInfo.name}</span>
                              </div>
                            )}
                          </div>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="start">
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openBookingSheet(day, hour); }}>
                            <Plus className="w-4 h-4 mr-2" /> New Booking
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openQuickBlock(day, hour); }}>
                            <Ban className="w-4 h-4 mr-2" /> Block Time
                          </DropdownMenuItem>
                          {blocked && blockInfo && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleRemoveBlock(blockInfo.id); }} className="text-destructive">
                                <Trash2 className="w-4 h-4 mr-2" /> Remove Block
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    );
                  })}
                  
                  {getBookingsForDay(day).map(booking => {
                    const { top, height } = getBookingPosition(booking);
                    const color = booking.service_color || '#3B82F6';
                    const isDragging = draggedBooking?.id === booking.id;
                    
                    return (
                      <div key={booking.id} draggable={booking.status === 'scheduled'} onDragStart={e => handleDragStart(e, booking)} onDragEnd={handleDragEnd}
                        className={cn("absolute left-1 right-1 rounded-lg px-2 py-1.5 text-sm overflow-hidden border-l-4 transition-all group",
                          booking.status === 'completed' && "bg-emerald-500/10 text-emerald-700",
                          booking.status === 'cancelled' && "bg-muted text-muted-foreground line-through opacity-50",
                          booking.status === 'no_show' && "bg-rose-500/10 text-rose-700",
                          booking.status === 'scheduled' && "bg-card shadow-sm cursor-grab",
                          isDragging && "opacity-50"
                        )}
                        style={{ top: `${top}px`, height: `${height}px`, minHeight: '32px', borderLeftColor: color }}>
                        <div className="font-semibold truncate leading-tight">{booking.client_display_name || booking.client_name}</div>
                        <div className="text-xs opacity-75 truncate">{booking.service_name}</div>
                        <div className="text-xs opacity-60 truncate flex items-center">
                          <Clock className="w-3 h-3 inline mr-1" />{format(parseISO(booking.start_at_utc), 'h:mm a')}
                          {booking.is_prepaid && <span className="ml-1.5 px-1.5 py-0.5 bg-fuchsia-500 text-white rounded text-[10px] font-bold tracking-wide">PREPAID</span>}
                        </div>
                        
                        {/* Context menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="absolute top-1 right-1 opacity-0 group-hover:opacity-100 p-0.5 hover:bg-muted rounded" onClick={e => e.stopPropagation()}>
                              <MoreVertical className="w-3 h-3" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {booking.status === 'scheduled' && (
                              <>
                                <DropdownMenuItem onClick={() => openEditBooking(booking)}>
                                  <Edit className="w-4 h-4 mr-2" /> Edit Booking
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleReconcileBooking(booking.id)}>
                                  <CheckCircle className="w-4 h-4 mr-2" /> Mark Complete
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openCancelDialog(booking)}>
                                  <RefreshCw className="w-4 h-4 mr-2" /> Reschedule
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => openCancelDialog(booking)} className="text-destructive">
                                  <XCircle className="w-4 h-4 mr-2" /> Cancel
                                </DropdownMenuItem>
                              </>
                            )}
                            {booking.status === 'completed' && (
                              <DropdownMenuItem disabled><CheckCircle className="w-4 h-4 mr-2" /> Completed</DropdownMenuItem>
                            )}
                            {(booking.status === 'cancelled' || booking.status === 'no_show') && (
                              <>
                                <DropdownMenuItem onClick={() => handleReinstateBooking(booking)}>
                                  <RotateCcw className="w-4 h-4 mr-2" /> Reinstate
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteBooking(booking.id)} className="text-destructive">
                                  <Trash2 className="w-4 h-4 mr-2" /> Remove
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          </div>
        ) : (
          /* Day View - Single day with larger time slots */
          <div className="max-w-4xl mx-auto">
            <div className="grid grid-cols-[100px_1fr] border-b sticky top-0 bg-background z-10">
              <div className="p-4 text-sm font-medium text-muted-foreground border-r">Time</div>
              <div className={`p-4 text-center ${isSameDay(currentDate, new Date()) ? 'bg-primary/5' : ''}`}>
                <div className="text-sm font-medium text-muted-foreground uppercase">{format(currentDate, 'EEEE')}</div>
                <div className={`text-2xl font-bold ${isSameDay(currentDate, new Date()) ? 'text-primary' : ''}`}>{format(currentDate, 'MMMM d')}</div>
              </div>
            </div>

            <div className="grid grid-cols-[100px_1fr]" onMouseUp={handleSelectionEnd} onMouseLeave={handleSelectionEnd}>
              <div className="border-r">
                {HOURS.map(hour => (
                  <div key={hour} className="h-[80px] border-b px-3 py-2">
                    <span className="text-base font-mono text-muted-foreground">{formatHour(hour)}</span>
                  </div>
                ))}
              </div>

              <div className="relative">
                {HOURS.map(hour => {
                  const outsideHours = isOutsideWorkingHours(currentDate, hour);
                  const blocked = isBlockedTime(currentDate, hour);
                  const blockInfo = blocked ? getBlockInfo(currentDate, hour) : null;
                  const isOver = dragOverSlot?.day?.toISOString() === currentDate.toISOString() && dragOverSlot?.hour === hour;
                  const inSelection = isSlotInSelection(currentDate, hour);
                  return (
                    <DropdownMenu key={hour}>
                      <DropdownMenuTrigger asChild>
                        <div 
                          className={cn(
                            "h-[80px] border-b transition-colors cursor-pointer select-none relative group",
                            outsideHours && !blocked && "bg-muted/50 hover:bg-muted/70",
                            blocked && "bg-rose-500/15 hover:bg-rose-500/25",
                            !outsideHours && !blocked && !inSelection && "hover:bg-muted/30",
                            isOver && "bg-primary/20",
                            inSelection && "bg-primary/30"
                          )} 
                          onClick={(e) => { if (!isSelecting && !e.defaultPrevented) openBookingSheet(currentDate, hour); }}
                          onMouseDown={() => handleSelectionStart(currentDate, hour)}
                          onMouseEnter={() => handleSelectionMove(currentDate, hour)}
                          onDragOver={e => handleDragOver(e, currentDate, hour)} 
                          onDrop={e => handleDrop(e, currentDate, hour)}
                          onContextMenu={(e) => e.preventDefault()}
                        >
                          {blocked && blockInfo && (
                            <div className="absolute inset-2 flex items-center justify-center">
                              <span className="text-sm font-medium text-rose-600 truncate px-2">{blockInfo.name}</span>
                            </div>
                          )}
                        </div>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="start">
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openBookingSheet(currentDate, hour); }}>
                          <Plus className="w-4 h-4 mr-2" /> New Booking
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={(e) => { e.stopPropagation(); openQuickBlock(currentDate, hour); }}>
                          <Ban className="w-4 h-4 mr-2" /> Block Time
                        </DropdownMenuItem>
                        {blocked && blockInfo && (
                          <>
                            <DropdownMenuSeparator />
                            <DropdownMenuItem onClick={(e) => { e.stopPropagation(); handleRemoveBlock(blockInfo.id); }} className="text-destructive">
                              <Trash2 className="w-4 h-4 mr-2" /> Remove Block
                            </DropdownMenuItem>
                          </>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  );
                })}
                
                {/* Day view bookings - larger cards with more details */}
                {getBookingsForDay(currentDate).map(booking => {
                  const startHour = parseISO(booking.start_at_utc).getHours();
                  const startMin = parseISO(booking.start_at_utc).getMinutes();
                  const top = (startHour - 7) * 80 + (startMin / 60) * 80;
                  const height = Math.max((booking.duration_min / 60) * 80, 50);
                  const color = booking.service_color || '#3B82F6';
                  const isDragging = draggedBooking?.id === booking.id;
                  const client = clients.find(c => c.id === booking.client_id);
                  
                  return (
                    <div key={booking.id} draggable={booking.status === 'scheduled'} onDragStart={e => handleDragStart(e, booking)} onDragEnd={handleDragEnd}
                      className={cn("absolute left-2 right-2 rounded-xl px-4 py-3 overflow-hidden border-l-4 transition-all group shadow-md",
                        booking.status === 'completed' && "bg-emerald-500/10 text-emerald-700",
                        booking.status === 'cancelled' && "bg-muted text-muted-foreground line-through opacity-50",
                        booking.status === 'no_show' && "bg-rose-500/10 text-rose-700",
                        booking.status === 'scheduled' && "bg-card cursor-grab",
                        isDragging && "opacity-50"
                      )}
                      style={{ top: `${top}px`, height: `${height}px`, minHeight: '50px', borderLeftColor: color }}>
                      <div className="flex justify-between items-start">
                        <div className="flex-1 min-w-0">
                          <div className="text-lg font-semibold truncate leading-tight">{booking.client_display_name || booking.client_name}</div>
                          <div className="text-sm opacity-80">{booking.service_name}</div>
                          <div className="text-sm opacity-70 flex items-center gap-3 mt-1">
                            <span className="flex items-center"><Clock className="w-4 h-4 mr-1" />{format(parseISO(booking.start_at_utc), 'h:mm a')} - {format(parseISO(booking.end_at_utc), 'h:mm a')}</span>
                            <span>({booking.duration_min} min)</span>
                            {booking.is_prepaid && <span className="px-2 py-0.5 bg-fuchsia-500 text-white rounded text-xs font-bold">PREPAID</span>}
                          </div>
                          {client?.email && <div className="text-xs opacity-60 mt-1">{client.email}</div>}
                          {booking.notes && <div className="text-xs opacity-60 mt-1 italic truncate">"{booking.notes}"</div>}
                        </div>
                        
                        {/* Context menu */}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button className="opacity-0 group-hover:opacity-100 p-1 hover:bg-muted rounded" onClick={e => e.stopPropagation()}>
                              <MoreVertical className="w-4 h-4" />
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            {booking.status === 'scheduled' && (
                              <>
                                <DropdownMenuItem onClick={() => openEditBooking(booking)}>
                                  <Edit className="w-4 h-4 mr-2" /> Edit Booking
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleReconcileBooking(booking.id)}>
                                  <CheckCircle className="w-4 h-4 mr-2" /> Mark Complete
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openCancelDialog(booking)}>
                                  <RefreshCw className="w-4 h-4 mr-2" /> Reschedule
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => openCancelDialog(booking)} className="text-destructive">
                                  <XCircle className="w-4 h-4 mr-2" /> Cancel
                                </DropdownMenuItem>
                              </>
                            )}
                            {booking.status === 'completed' && (
                              <DropdownMenuItem disabled><CheckCircle className="w-4 h-4 mr-2" /> Completed</DropdownMenuItem>
                            )}
                            {(booking.status === 'cancelled' || booking.status === 'no_show') && (
                              <>
                                <DropdownMenuItem onClick={() => handleReinstateBooking(booking)}>
                                  <RotateCcw className="w-4 h-4 mr-2" /> Reinstate
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteBooking(booking.id)} className="text-destructive">
                                  <Trash2 className="w-4 h-4 mr-2" /> Remove
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Reinstate Warning Dialog */}
      <Dialog open={reinstateWarningOpen} onOpenChange={setReinstateWarningOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-500" />
              Reinstate Booking
            </DialogTitle>
            <DialogDescription>
              You are about to reinstate this booking back to scheduled status.
            </DialogDescription>
          </DialogHeader>
          {bookingToReinstate && (
            <div className="p-3 bg-muted rounded-lg">
              <p className="font-medium">{bookingToReinstate.client_name}</p>
              <p className="text-sm text-muted-foreground">
                {bookingToReinstate.service_name} • {format(parseISO(bookingToReinstate.start_at_utc), 'PPP p')}
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => { setReinstateWarningOpen(false); setBookingToReinstate(null); }}>Cancel</Button>
            <Button onClick={confirmReinstateBooking}>Reinstate Booking</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
